/**
 * Wrappers tipados para los endpoints /api/auth/* y /api/me.
 * Usar desde componentes / hooks de auth (AuthProvider, formularios).
 */

import { apiClient } from './apiClient'
import type { StoredUser } from '../auth/tokenStore'

export type TokenPair = {
  access_token: string
  refresh_token: string
  access_expires_in: number
  refresh_expires_in: number
  user: StoredUser
}

export type RegisterInitiateResponse = {
  message: string
  next_step: 'verify_otp'
  email: string
}

export type RegisterCompletePayload = {
  verified_email_token: string
  password: string
  first_name: string
  last_name_paternal: string
  last_name_maternal?: string
  accepted_legal: boolean
}

export type LoginPayload = {
  email: string
  password: string
}

export type OtpVerifyPayload = {
  email: string
  code: string
  purpose: 'signup' | 'password_reset'
}

export type OtpResetTicketResponse = {
  reset_ticket: string
}

export type PasswordResetRequestPayload = {
  email: string
}

export type PasswordResetConfirmPayload = {
  reset_ticket: string
  new_password: string
}

export const authClient = {
  registerInitiate: (email: string) =>
    apiClient.post<RegisterInitiateResponse>('/auth/register/initiate', { email }, { skipAuth: true }),

  registerComplete: (payload: RegisterCompletePayload) =>
    apiClient.post<TokenPair>('/auth/register/complete', payload, { skipAuth: true }),

  login: (payload: LoginPayload) =>
    apiClient.post<TokenPair>('/auth/login', payload, { skipAuth: true }),

  /** Login del admin de plataforma — endpoint y tabla separados (§auth admins). */
  loginAdmin: (payload: LoginPayload) =>
    apiClient.post<TokenPair>('/admin/auth/login', payload, { skipAuth: true }),

  /** Verifica OTP. Para signup → devuelve TokenPair; para reset → devuelve reset_ticket. */
  verifyOtp: (payload: OtpVerifyPayload) =>
    apiClient.post<TokenPair | OtpResetTicketResponse>('/auth/otp/verify', payload, { skipAuth: true }),

  resendOtp: (email: string, purpose: 'signup' | 'password_reset') =>
    apiClient.post<{ message: string }>('/auth/otp/resend', { email, purpose }, { skipAuth: true }),

  requestPasswordReset: (payload: PasswordResetRequestPayload) =>
    apiClient.post<{ message: string }>('/auth/password-reset/request', payload, { skipAuth: true }),

  confirmPasswordReset: (payload: PasswordResetConfirmPayload) =>
    apiClient.post<{ message: string }>('/auth/password-reset/confirm', payload, { skipAuth: true }),

  logout: () => apiClient.post<void>('/auth/logout'),

  /** Datos del usuario actual (revalida la sesión). */
  me: () => apiClient.get<{ user: StoredUser }>('/me'),
}
